class HanacureGwpController {
  constructor(options) {
    this.options = options || {};
    this.root = this.options.root || document;
    this.containerSelector =
      this.options.containerSelector || "[data-gwp-unlock-base-cents]";
    this.modalSelector = this.options.modalSelector || "[data-gwp-modal]";
    this.openSelector = this.options.openSelector || "[data-gwp-open]";
    this.closeSelector = this.options.closeSelector || "[data-gwp-close]";
    this.addSelector = this.options.addSelector || "[data-gwp-add-variant]";
    this.gridSelector = this.options.gridSelector || "[data-gwp-grid]";
    this.emptyMessageClass = this.options.emptyMessageClass || "gwp-item__name";
    this.incompleteAmountTag = this.options.incompleteAmountTag || "b";
    this.giftButtonDefaultText =
      this.options.giftButtonDefaultText || "Add free gift";
    this.giftButtonLoadingText =
      this.options.giftButtonLoadingText || "Adding...";
    this.removeIneligibleOnInit = !!this.options.removeIneligibleOnInit;

    this.onClick = this.onClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);
  }

  init() {
    if (this.bound || !this.getContainer()) {
      return;
    }

    this.bound = true;
    this.root.addEventListener("click", this.onClick);
    this.root.addEventListener("keydown", this.onKeydown);

    this.refresh();

    if (this.removeIneligibleOnInit) {
      this.removeIneligibleAfterCartLoad().then((removed) => {
        if (removed && typeof this.options.onIneligibleRemoved === "function") {
          this.options.onIneligibleRemoved();
        }
      });
    }
  }

  destroy() {
    if (!this.bound) {
      return;
    }

    this.bound = false;
    this.root.removeEventListener("click", this.onClick);
    this.root.removeEventListener("keydown", this.onKeydown);
  }

  refresh() {
    if (!this.getContainer()) {
      return;
    }

    this.loadGwpOptions();
    this.updateGwpUnlockUI();
  }

  getContainer() {
    return this.root.querySelector(this.containerSelector);
  }

  getModal() {
    return this.root.querySelector(this.modalSelector);
  }

  parseVariantIds(csv) {
    return String(csv || "")
      .split(",")
      .map((id) => id.trim())
      .filter((id) => id.length > 0)
      .slice(0, 4);
  }

  getGwpVariantIds() {
    const container = this.getContainer();
    if (!container) {
      return [];
    }

    return this.parseVariantIds(container.getAttribute("data-gwp-variant-ids"));
  }

  isGwpVariantId(variantId) {
    return this.getGwpVariantIds().indexOf(String(variantId)) !== -1;
  }

  escapeHtml(str) {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  openModal() {
    const modal = this.getModal();
    if (!modal) {
      return;
    }
    modal.hidden = false;
    document.body.style.overflow = "hidden";
  }

  closeModal() {
    const modal = this.getModal();
    if (!modal) {
      return;
    }
    modal.hidden = true;
    document.body.style.overflow = "";
  }

  firstProductImage(product) {
    if (!product) {
      return "";
    }
    if (product.image && product.image.src) {
      return product.image.src;
    }
    if (product.images && product.images.length) {
      if (typeof product.images[0] === "string") {
        return product.images[0];
      }
      if (product.images[0] && product.images[0].src) {
        return product.images[0].src;
      }
    }
    return "";
  }

  getGlobalProducts() {
    if (!window.__hanacureProductsJsonPromise) {
      window.__hanacureProductsJsonPromise = fetch("/products.json?limit=250", {
        headers: { Accept: "application/json" },
      })
        .then((response) => {
          if (!response.ok) {
            return { products: [] };
          }
          return response.json();
        })
        .catch(() => ({ products: [] }));
    }

    return window.__hanacureProductsJsonPromise;
  }

  renderGwpGrid(items) {
    const modal = this.getModal();
    if (!modal) {
      return;
    }

    const grid = modal.querySelector(this.gridSelector);
    if (!grid) {
      return;
    }

    if (!items.length) {
      grid.innerHTML = `<p class="${this.escapeHtml(this.emptyMessageClass)}">No free gift options are available right now.</p>`;
      return;
    }

    grid.innerHTML = items
      .map((item) => {
        const image = item && item.image ? item.image : "";
        const title = item && item.title ? item.title : "Free gift";

        return (
          `<div class="cd-gwp-item cp-gwp-item">${
            image
              ? `<img src="${this.escapeHtml(image)}" alt="${this.escapeHtml(title)}" loading="lazy">`
              : ""
          }<div class="cd-gwp-item__name cp-gwp-item__name">${this.escapeHtml(title)}</div>` +
          `<button type="button" class="cd-gwp-item__btn cp-gwp-item__btn" data-gwp-add-variant="${this.escapeHtml(
            item.id,
          )}">${this.escapeHtml(this.giftButtonDefaultText)}</button>` +
          `</div>`
        );
      })
      .join("");
  }

  loadGwpOptions() {
    if (this.gwpOptionsPromise) {
      return this.gwpOptionsPromise;
    }

    const ids = this.getGwpVariantIds();
    if (!ids.length) {
      this.renderGwpGrid([]);
      return Promise.resolve();
    }

    this.gwpOptionsPromise = this.getGlobalProducts()
      .then((data) => {
        const products = (data && data.products) || [];
        const wanted = {};
        ids.forEach((id) => {
          wanted[String(id)] = true;
        });

        const matchedById = {};

        products.forEach((product) => {
          const productImage = this.firstProductImage(product);
          const productTitle =
            product && product.title ? product.title : "Free gift";

          (product.variants || []).forEach((variant) => {
            const variantId = String(variant.id);
            if (!wanted[variantId]) {
              return;
            }

            let cardTitle = productTitle;
            if (variant.title && variant.title !== "Default Title") {
              cardTitle = `${productTitle} | ${variant.title}`;
            }

            matchedById[variantId] = {
              id: variant.id,
              title: cardTitle,
              image: productImage,
            };
          });
        });

        const ordered = ids
          .map((id) => matchedById[String(id)] || null)
          .filter((item) => !!item);

        this.renderGwpGrid(ordered);
      })
      .catch(() => {
        this.renderGwpGrid([]);
      })
      .finally(() => {
        this.gwpOptionsPromise = null;
      });

    return this.gwpOptionsPromise;
  }

  getCurrencyRate() {
    const currency =
      window.Shopify && window.Shopify.currency
        ? window.Shopify.currency
        : null;
    const rawRate = currency && currency.rate ? Number(currency.rate) : NaN;

    if (!Number.isFinite(rawRate) || rawRate <= 0) {
      return 1;
    }

    return rawRate;
  }

  formatMoney(cents) {
    const value = Number(cents);
    if (window.Shopify && typeof window.Shopify.formatMoney === "function") {
      return window.Shopify.formatMoney(Number.isFinite(value) ? value : 0);
    }

    const currency =
      window.Shopify &&
      window.Shopify.currency &&
      window.Shopify.currency.active
        ? window.Shopify.currency.active
        : "USD";

    return ((Number.isFinite(value) ? value : 0) / 100).toLocaleString(
      "en-US",
      {
        style: "currency",
        currency,
        minimumFractionDigits: 2,
      },
    );
  }

  renderIncompleteMessage(template, formattedAmount) {
    const placeholder = "__GWP_AMOUNT__";
    const withPlaceholder = String(template || "")
      .replace(/\[amount\]/g, placeholder)
      .replace(/\[AMOUNT\]/g, placeholder);
    const escaped = this.escapeHtml(withPlaceholder);
    const amountHtml = `<${this.incompleteAmountTag}>${this.escapeHtml(formattedAmount)}</${this.incompleteAmountTag}>`;
    return escaped.replace(new RegExp(placeholder, "g"), amountHtml);
  }

  updateGwpUnlockUI() {
    const container = this.getContainer();
    if (!container) {
      return;
    }

    const baseUnlockCents = Number(
      container.getAttribute("data-gwp-unlock-base-cents"),
    );
    const eligibleTotalCents = Number(
      container.getAttribute("data-gwp-eligible-total-cents"),
    );
    const hasGift = container.getAttribute("data-gwp-has-gift") === "true";

    if (!Number.isFinite(baseUnlockCents) || baseUnlockCents <= 0) {
      return;
    }

    const convertedUnlockCents = Math.max(
      1,
      Math.round(baseUnlockCents * this.getCurrencyRate()),
    );
    const totalCents = Number.isFinite(eligibleTotalCents)
      ? eligibleTotalCents
      : 0;
    const remainingCents = Math.max(0, convertedUnlockCents - totalCents);
    const fillPct = Math.min(
      100,
      Math.max(0, Math.round((totalCents * 100) / convertedUnlockCents)),
    );
    const isUnlocked = totalCents >= convertedUnlockCents;

    const addedState = container.querySelector("[data-gwp-state-added]");
    const unlockedState = container.querySelector("[data-gwp-state-unlocked]");
    const lockedState = container.querySelector("[data-gwp-state-locked]");
    const progressTrack = container.querySelector("[data-gwp-progress-track]");
    const progressFill = container.querySelector("[data-gwp-progress-fill]");
    const incompleteText = container.querySelector(
      "[data-gwp-incomplete-text]",
    );
    const completeText = container.querySelector("[data-gwp-complete-text]");

    if (addedState) {
      addedState.hidden = !(isUnlocked && hasGift);
    }
    if (unlockedState) {
      unlockedState.hidden = !isUnlocked || hasGift;
    }
    if (lockedState) {
      lockedState.hidden = isUnlocked;
    }
    if (progressTrack) {
      progressTrack.hidden = isUnlocked && hasGift;
    }
    if (progressFill) {
      progressFill.style.width = `${fillPct}%`;
    }

    const incompleteTemplate =
      container.getAttribute("data-gwp-incomplete-message") ||
      "You are [amount] away from gift unlocking";
    if (incompleteText) {
      incompleteText.innerHTML = this.renderIncompleteMessage(
        incompleteTemplate,
        this.formatMoney(remainingCents),
      );
    }

    const completeMessage =
      container.getAttribute("data-gwp-complete-message") ||
      "You have unlocked gift.";
    if (completeText) {
      completeText.textContent = completeMessage;
    }
  }

  removeLine(line) {
    return fetch("/cart/change.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Accept: "application/json",
      },
      body: JSON.stringify({ line: line, quantity: 0 }),
    }).then((response) => response.json());
  }

  removeExistingGwpItems(gwpIds) {
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then((response) => response.json())
      .then((cart) => {
        const linesToRemove = [];
        (cart.items || []).forEach((item, index) => {
          const variantId = String(item.id);
          let isGwp =
            item.properties && item.properties._gwp_free_gift === "true";
          if (!isGwp && gwpIds.indexOf(variantId) !== -1) {
            isGwp = true;
          }
          if (isGwp) {
            linesToRemove.push(index + 1);
          }
        });

        linesToRemove.sort((a, b) => b - a);

        return linesToRemove.reduce((promise, line) => {
          return promise.then(() => this.removeLine(line));
        }, Promise.resolve());
      });
  }

  getUnlockThresholdCents() {
    const container = this.getContainer();
    if (!container) {
      return null;
    }

    const baseUnlockCents = Number(
      container.getAttribute("data-gwp-unlock-base-cents"),
    );
    if (!Number.isFinite(baseUnlockCents) || baseUnlockCents <= 0) {
      return null;
    }

    return Math.max(1, Math.round(baseUnlockCents * this.getCurrencyRate()));
  }

  isConfiguredGwpItem(item, gwpIds) {
    if (!item) {
      return false;
    }

    if (item.properties && item.properties._gwp_free_gift === "true") {
      return true;
    }

    return gwpIds.indexOf(String(item.id)) !== -1;
  }

  getEligibleCartTotalExcludingGwp(cart, gwpIds) {
    return ((cart && cart.items) || []).reduce((sum, item) => {
      if (this.isConfiguredGwpItem(item, gwpIds)) {
        return sum;
      }

      const lineTotal = Number(item && item.final_line_price);
      if (!Number.isFinite(lineTotal)) {
        return sum;
      }

      return sum + lineTotal;
    }, 0);
  }

  removeIneligibleAfterCartChange(cart) {
    const gwpIds = this.getGwpVariantIds();
    if (!gwpIds.length) {
      return Promise.resolve(false);
    }

    const unlockThresholdCents = this.getUnlockThresholdCents();
    if (!unlockThresholdCents) {
      return Promise.resolve(false);
    }

    const eligibleCartTotal = this.getEligibleCartTotalExcludingGwp(
      cart,
      gwpIds,
    );
    if (eligibleCartTotal >= unlockThresholdCents) {
      return Promise.resolve(false);
    }

    const hasGwpInCart = ((cart && cart.items) || []).some((item) =>
      this.isConfiguredGwpItem(item, gwpIds),
    );
    if (!hasGwpInCart) {
      return Promise.resolve(false);
    }

    return this.removeExistingGwpItems(gwpIds).then(() => true);
  }

  removeIneligibleAfterCartLoad() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then((response) => response.json())
      .then((cart) => this.removeIneligibleAfterCartChange(cart));
  }

  addFreeGiftVariant(variantId) {
    const gwpIds = this.getGwpVariantIds();
    return this.removeExistingGwpItems(gwpIds)
      .then(() => {
        return fetch("/cart/add.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            items: [
              {
                id: Number(variantId),
                quantity: 1,
                properties: { _gwp_free_gift: "true" },
              },
            ],
          }),
        });
      })
      .then((response) => {
        if (!response.ok) {
          return response.json().then((error) => {
            throw error;
          });
        }
        return response.json();
      });
  }

  onClick(event) {
    const openBtn = event.target.closest(this.openSelector);
    if (openBtn && this.root.contains(openBtn)) {
      event.preventDefault();
      this.openModal();
      return;
    }

    const closeBtn = event.target.closest(this.closeSelector);
    if (closeBtn && this.root.contains(closeBtn)) {
      event.preventDefault();
      this.closeModal();
      return;
    }

    const addGiftBtn = event.target.closest(this.addSelector);
    if (!addGiftBtn || !this.root.contains(addGiftBtn)) {
      return;
    }

    event.preventDefault();
    const variantId = addGiftBtn.getAttribute("data-gwp-add-variant");
    if (!variantId || addGiftBtn.disabled) {
      return;
    }

    const modal = this.getModal();
    const allAddButtons = modal ? modal.querySelectorAll(this.addSelector) : [];
    allAddButtons.forEach((button) => {
      button.disabled = true;
    });
    addGiftBtn.textContent = this.giftButtonLoadingText;

    this.addFreeGiftVariant(variantId)
      .then(() => {
        this.closeModal();
        if (typeof this.options.onGiftAdded === "function") {
          return this.options.onGiftAdded();
        }
        window.location.reload();
      })
      .catch(() => {
        allAddButtons.forEach((button) => {
          button.disabled = false;
        });
        addGiftBtn.textContent = this.giftButtonDefaultText;
      });
  }

  onKeydown(event) {
    if (event.key === "Escape") {
      this.closeModal();
    }
  }
}

window.HanacureGwpController = HanacureGwpController;
